import Paper from '@/components/Paper'
import useUploadFile from '@/hooks/useUploadFile'
import CheckIcons from '@/icons/CheckIcons'
import EditIcons from '@/icons/EditIcons'
import EmailIcons from '@/icons/EmailIcons'
import AgeIcons from '@/icons/AgeIcons'
import PhoneIcons from '@/icons/PhoneIcons'
import GenderIcons from '@/icons/GenderIcons'
import { EMPLOYEE_CONTRACT_TYPE } from '@/modules/Employee/constants'
import {
    activeAccountEmployee,
    getEmployeeAvatar,
    updateEmployeeAvatar,
} from '@/modules/Employee/services'
import { Employee } from '@/modules/Employee/types/response'
import { Attachment } from '@/modules/Upload/types'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Typogaphy } from 'vnetwork-ui'
import TypeOfHireIcons from '@/icons/TypeOfHireIcons'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import toast from 'react-hot-toast'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface ProfileHeaderProps {
    employee: Employee
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ employee }) => {
    const { t } = useTranslation()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [currentAvatar, setCurrentAvatar] = useState<string | null>(null)
    const { refetch } = useQueryClientCallback({
        mode: 'contains',
        pattern: 'employees',
    })

    const { data: avatar } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.EMPLOYEES, 'avatar', employee.employee_id],
        queryFn: () => getEmployeeAvatar(employee.employee_id),
    })

    const { isUploading, uploadAsync } = useUploadFile({
        prefix: 'employees/avatars',
        isPublic: true,
    })

    const { mutateAsync: activeAccount, isPending: isActivating } =
        useMutationWithHandler(
            {
                mutationFn: activeAccountEmployee,
            },
            {
                reactQueryKeys: QUERY_KEYS.EMPLOYEES,
                successMessage: 'account-activated-successfully',
                errorMessage: 'account-activation-failed',
                onSuccess: () => {
                    refetch()
                },
            }
        )

    const { mutateAsync: updateAvatar, isPending: isUpdatingAvatar } =
        useMutationWithHandler(
            {
                mutationFn: updateEmployeeAvatar,
            },
            {
                reactQueryKeys: QUERY_KEYS.EMPLOYEES,
                successMessage: 'avatar-updated-successfully',
                errorMessage: 'avatar-update-failed',
                onSuccess: () => {
                    refetch()
                },
            }
        )

    const getAvatarUrl = (email: string) => {
        if (!email) return null
        return `https://api.dicebear.com/9.x/thumbs/svg?seed=${encodeURIComponent(email)}`
    }

    const getInitials = (name: string) => {
        return (
            name
                ?.split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2) || 'EM'
        )
    }

    const calculateAge = (birthDate: string) => {
        if (!birthDate) return null
        const today = new Date()
        const birth = new Date(birthDate)
        let age = today.getFullYear() - birth.getFullYear()
        const monthDiff = today.getMonth() - birth.getMonth()

        if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birth.getDate())
        ) {
            age--
        }

        return age
    }

    const handleSelectFile = () => {
        fileInputRef.current?.click()
    }

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0]
        if (file) {
            if (!file.type.startsWith('image/')) {
                toast.error(t('please-select-image-file'))
                return
            }

            if (file.size > 10 * 1024 * 1024) {
                toast.error(t('file-size-too-large'))
                return
            }

            try {
                const attachmentUploadeds = await uploadAsync([file])
                const attachments = attachmentUploadeds
                    ?.filter((attachment) => attachment.status === 'fulfilled')
                    .map((attachment) => attachment.value) as Attachment[]

                if (attachments) {
                    await updateAvatar({
                        employee_id: employee.employee_id,
                        avatar_url: attachments[0].attachment_file_url,
                    })
                    setCurrentAvatar(attachments[0].attachment_file_url)
                }
            } catch (error) {
                console.error('Avatar update failed:', error)
            }
        }
        event.target.value = ''
    }

    const handleActiveAccount = async () => {
        if (!employee?.employee_id) return

        try {
            await activeAccount({
                employee_id: employee.employee_id,
            })
        } catch (error) {
            console.error('Active account failed:', error)
        }
    }

    const displayAvatar =
        currentAvatar ||
        (avatar ? `${import.meta.env.APP_STORAGE}/${avatar}` : null) ||
        getAvatarUrl(employee?.employee_email_company || '')

    return (
        <Paper className="shadow-lg h-full flex flex-col bg-white rounded-xl overflow-hidden">
            {/* Header with gradient background */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 relative overflow-hidden">
                {/* Decorative curved lines background */}
                <div className="absolute inset-0 opacity-30">
                    <svg
                        viewBox="0 0 400 200"
                        className="absolute top-0 right-0 w-full h-full"
                    >
                        <defs>
                            <linearGradient
                                id="lineGradient"
                                x1="0%"
                                y1="0%"
                                x2="100%"
                                y2="100%"
                            >
                                <stop
                                    offset="0%"
                                    stopColor="#ffffff"
                                    stopOpacity="0.5"
                                />
                                <stop
                                    offset="100%"
                                    stopColor="#ffffff"
                                    stopOpacity="0.1"
                                />
                            </linearGradient>
                        </defs>
                        {/* Curved lines */}
                        <path
                            d="M100,50 Q200,20 300,50 Q400,80 500,50"
                            stroke="url(#lineGradient)"
                            strokeWidth="2"
                            fill="none"
                        />
                        <path
                            d="M80,70 Q180,40 280,70 Q380,100 480,70"
                            stroke="url(#lineGradient)"
                            strokeWidth="2"
                            fill="none"
                        />
                        <path
                            d="M120,90 Q220,60 320,90 Q420,120 520,90"
                            stroke="url(#lineGradient)"
                            strokeWidth="2"
                            fill="none"
                        />
                        <path
                            d="M60,110 Q160,80 260,110 Q360,140 460,110"
                            stroke="url(#lineGradient)"
                            strokeWidth="2"
                            fill="none"
                        />
                        <path
                            d="M140,130 Q240,100 340,130 Q440,160 540,130"
                            stroke="url(#lineGradient)"
                            strokeWidth="2"
                            fill="none"
                        />
                        <path
                            d="M40,150 Q140,120 240,150 Q340,180 440,150"
                            stroke="url(#lineGradient)"
                            strokeWidth="2"
                            fill="none"
                        />
                    </svg>
                </div>
                {/* Edit button - top right */}
                <div
                    className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-md cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={handleSelectFile}
                    title={t('edit-avatar')}
                >
                    <EditIcons className="w-4 h-4 text-gray-600" />
                </div>

                {/* Avatar */}
                <div className="flex justify-center mb-4">
                    <div className="relative">
                        <div className="w-20 h-20 rounded-full overflow-hidden flex items-center justify-center bg-gradient-to-br from-blue-400 to-blue-600 relative border-4 border-white shadow-lg">
                            {displayAvatar ? (
                                <img
                                    src={displayAvatar}
                                    alt={employee?.employee_name || 'Employee'}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        const target =
                                            e.target as HTMLImageElement
                                        target.style.display = 'none'
                                        const fallback =
                                            target.parentElement?.querySelector(
                                                '.fallback-initials'
                                            ) as HTMLElement
                                        if (fallback)
                                            fallback.style.display = 'flex'
                                    }}
                                />
                            ) : null}
                            <span
                                className={`fallback-initials text-lg font-bold text-white absolute inset-0 ${displayAvatar ? 'hidden' : 'flex'} items-center justify-center`}
                            >
                                {getInitials(employee?.employee_name || '')}
                            </span>

                            {/* Loading overlay */}
                            {(isUploading || isUpdatingAvatar) && (
                                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-full">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                </div>
                            )}
                        </div>

                        {/* Hidden file input */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="hidden"
                            disabled={isUploading || isUpdatingAvatar}
                        />
                    </div>
                </div>

                {/* Employee Name & ID */}
                <div className="text-center">
                    <Typogaphy className="font-semibold text-xl text-gray-900 mb-1">
                        {employee?.employee_name || 'N/A'}
                    </Typogaphy>
                    <div className="flex items-center justify-center gap-2 mb-1">
                        <Typogaphy className="text-gray-600 font-medium">
                            {employee?.employee_code || 'N/A'}
                        </Typogaphy>
                        <button className="text-gray-400 hover:text-gray-600">
                            <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"></path>
                                <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"></path>
                            </svg>
                        </button>
                    </div>
                    <Typogaphy className="text-blue-600 text-sm">
                        {employee?.position_name || 'N/A'}
                    </Typogaphy>
                </div>
            </div>

            {/* Basic Information */}
            <div className="flex-1 p-6">
                <Typogaphy className="font-semibold text-gray-900 text-lg mb-4">
                    {t('basic-information')}
                </Typogaphy>
                <div className="space-y-4">
                    {/* Email */}
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <EmailIcons className="w-4 h-4 text-gray-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <Typogaphy className="text-sm text-gray-500 mb-0.5">
                                {t('email')}
                            </Typogaphy>
                            <Typogaphy className="font-medium text-gray-900 truncate">
                                {employee?.employee_email_company || 'N/A'}
                            </Typogaphy>
                        </div>
                    </div>

                    {/* Mobile Phone */}
                    {(employee as any)?.employee_phone_number && (
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <PhoneIcons className="w-4 h-4 text-gray-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <Typogaphy className="text-sm text-gray-500 mb-0.5">
                                    {t('phone')}
                                </Typogaphy>
                                <Typogaphy className="font-medium text-gray-900">
                                    {(employee as any).employee_phone_number}
                                </Typogaphy>
                            </div>
                        </div>
                    )}

                    {/* Gender */}
                    {(employee as any)?.employee_gender && (
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <GenderIcons className="w-4 h-4 text-gray-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <Typogaphy className="text-sm text-gray-500 mb-0.5">
                                    {t('gender')}
                                </Typogaphy>
                                <Typogaphy className="font-medium text-gray-900 capitalize">
                                    {(employee as any).employee_gender}
                                </Typogaphy>
                            </div>
                        </div>
                    )}

                    {/* Age */}
                    {employee?.employee_birthday && (
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <AgeIcons className="w-4 h-4 text-gray-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <Typogaphy className="text-sm text-gray-500 mb-0.5">
                                    {t('age')}
                                </Typogaphy>
                                <Typogaphy className="font-medium text-gray-900">
                                    {calculateAge(employee.employee_birthday)}
                                </Typogaphy>
                            </div>
                        </div>
                    )}

                    {/* Status */}
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <div
                                className={`w-2 h-2 rounded-full ${(employee as any)?.employee_disable ? 'bg-red-500' : 'bg-green-500'}`}
                            ></div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <Typogaphy className="text-sm text-gray-500 mb-0.5">
                                {t('status')}
                            </Typogaphy>
                            <Typogaphy
                                className={`font-medium ${(employee as any)?.employee_disable ? 'text-red-700' : 'text-green-700'}`}
                            >
                                {(employee as any)?.employee_disable
                                    ? t('account-disabled')
                                    : t('account-active')}
                            </Typogaphy>
                        </div>
                    </div>

                    {/* Employee Type */}
                    {(employee as any)?.employee_contract_type && (
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <TypeOfHireIcons className="w-4 h-4 text-gray-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <Typogaphy className="text-sm text-gray-500 mb-0.5">
                                    {t('employee_contract_type')}
                                </Typogaphy>
                                <Typogaphy className="font-medium text-gray-900">
                                    {EMPLOYEE_CONTRACT_TYPE(t)?.find(
                                        (type: any) =>
                                            type.value ===
                                            (employee as any)
                                                ?.employee_contract_type
                                    )?.label ||
                                        (employee as any)
                                            ?.employee_contract_type}
                                </Typogaphy>
                            </div>
                        </div>
                    )}

                    {/* Account Confirmation */}
                    {!(employee as any)?.employee_confirmed && (
                        <div className="mt-6">
                            <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<CheckIcons />}
                                loading={isActivating}
                                disabled={isActivating}
                                onClick={handleActiveAccount}
                                className="w-full"
                            >
                                {t('activation-account')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Paper>
    )
}

export default ProfileHeader
